import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FaCheck, FaChevronRight, FaPlus } from "react-icons/fa";
import "./check-out.scss";
import { toast } from "react-toastify";
import ApplyPromoCode from "../../ui/commons/ApplyPromoCode";
import { requestFlightsPreorder } from "../../../requests/flightsAPI";
import { useSelector, useDispatch } from "react-redux";
import { get, post } from "../../../utils/request";
import PassengerCard from "../Passenger/PassengerCard";
import ContactInfoCard from "../Passenger/ContactInfoCard";
import ErrorModal from "../../ui/commons/ErrorModal";
import { AppError, UnauthorizedError } from "../../../utils/customError";
import { useNavigate, useLocation } from "react-router-dom";
import { buildFlightsUrl } from "../../Flights/Flights";
import { getTimezone } from "../../../utils/context";
import LoginModal from "../../../components/Auth/LoginModal";
import RevalidateDialog from "./RevalidateDialog";
import {
  FLIGHT_REVALIDATION_ACTION,
  getFlightRevalidationAction,
} from "./flightRevalidation";

const CheckoutForm = ({
  flightSearchParams,
  flightsPreorderResult,
  selectedItinerary,
}) => {
  const userData = useSelector((state) => state.userData.user);

  const navigate = useNavigate();

  const [selectedCard, setSelectedCard] = useState(0);
  const [loading, setLoading] = useState(false);

  const [passengers, setPassengers] = useState([]);
  const [contactInfo, setContactInfo] = useState(null);
  const [selectedPassengers, setSelectedPassengers] = useState({});
  const [passengersLoading, setPassengersLoading] = useState(true);
  const [contactLoading, setContactLoading] = useState(true);

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [orderId, setOrderId] = useState(null);

  const [openLoginModal, setOpenLoginModal] = useState(false);
  const [revalidateDialog, setRevalidateDialog] = useState(null);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  useEffect(() => {
    if (!userData) return;
    fetchPassengers();
  }, [userData]);

  useEffect(() => {
    if (!userData) return;
    fetchContactInfo();
  }, [userData]);

  async function fetchPassengers() {
    setPassengersLoading(true);
    try {
      const result = await get("/passengers/getAll");
      setPassengers(result);
    } catch (e) {
      setPassengers([]);
    } finally {
      setPassengersLoading(false);
    }
  }

  async function fetchContactInfo() {
    setContactLoading(true);
    try {
      const result = await get("/contactInfo/getDefault");
      setContactInfo(result);
    } catch (e) {
      setContactInfo(null);
    } finally {
      setContactLoading(false);
    }
  }

  const cards = [
    {
      id: 0,
      logo: "/images/icons/visa-icon.svg",
      masked: "**** **** **** 1234",
      cvv: "123",
      expiry: "12/1997",
    },
    {
      id: 1,
      logo: "/images/icons/dinners.svg",
      masked: "**** **** 1234",
      cvv: "123",
      expiry: "12/1997",
    },
    {
      id: 2,
      logo: "/images/icons/mastercard-icon.svg",
      masked: "**** **** **** 1234",
      cvv: "123",
      expiry: "12/1997",
    },
  ];

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    return parts.length ? parts.join(" ") : value;
  };

  const formatExpiryDate = (value) => {
    const v = value.replace(/[^0-9]/g, "");
    if (v.length >= 3) {
      return `${v.substring(0, 2)}/${v.substring(2, 4)}`;
    }
    return value;
  };

  const guestFormik = useFormik({
    initialValues: {
      givenNames: "",
      surname: "",
      gender: "",
      nationality: "",
      dob: "",
    },
    validationSchema: Yup.object({
      givenNames: Yup.string()
        .matches(/^[A-Za-z\s]+$/, "Only English letters allowed")
        .required("First name is required"),
      surname: Yup.string()
        .matches(/^[A-Za-z\s]+$/, "Only English letters allowed")
        .required("Last name is required"),
      gender: Yup.string()
        .oneOf(["male", "female"], "Please select gender")
        .required("Gender is required"),
      nationality: Yup.string().required("Nationality is required"),
      dob: Yup.date()
        .required("Date of birth is required")
        .max(new Date(), "Date of birth cannot be in the future")
        .test("age", "You must be at least 18 years old", (value) => {
          const cutoff = new Date();
          cutoff.setFullYear(cutoff.getFullYear() - 18);
          return value <= cutoff;
        })
        .typeError("Invalid date format (YYYY-MM-DD)"),
    }),
    onSubmit: (values) => {
      console.log("Form values:", values);
    },
  });

  const cardFormik = useFormik({
    initialValues: {
      card_email: "",
      card_number: "",
      card_expiry: "",
      card_cvc: "",
    },
    validationSchema: Yup.object({
      card_email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),

      card_number: Yup.string()
        .matches(
          /^\d{4} \d{4} \d{4} \d{4}$/,
          "Card number must be in the format: 1234 1234 1234 1234"
        )
        .required("Card number is required"),

      card_expiry: Yup.string()
        .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, "Expiry must be in MM/YY format")
        .test("expiry-date", "Card has expired", function (value) {
          if (!value) return false;
          const [month, year] = value.split("/");
          const currentYear = new Date().getFullYear() % 100;
          const currentMonth = new Date().getMonth() + 1;

          const expMonth = parseInt(month, 10);
          const expYear = parseInt(year, 10);

          return (
            expYear > currentYear ||
            (expYear === currentYear && expMonth >= currentMonth)
          );
        })
        .required("Expiry date is required"),

      card_cvc: Yup.string()
        .matches(/^\d{3,4}$/, "CVC must be 3 or 4 digits")
        .required("CVC is required"),
    }),
    onSubmit: (values) => {
      console.log("Card Submitted:", values);
    },
  });

  const handleCardNumberChange = (e) => {
    const formattedValue = formatCardNumber(e.target.value);
    cardFormik.setFieldValue("card_number", formattedValue);
  };

  const handleExpiryChange = (e) => {
    const formattedValue = formatExpiryDate(e.target.value);
    cardFormik.setFieldValue("card_expiry", formattedValue);
  };

  const [preorderResult, setPreorderResult] = useState(
    flightsPreorderResult || null
  );

  useEffect(() => {
    setPreorderResult(flightsPreorderResult || null);
  }, [flightsPreorderResult]);

  const onApply = async (promoCode) => {
    preorderResult.preorderInfo.promoCode = promoCode;
    const newPreorderResult = await requestFlightsPreorder(
      preorderResult.preorderInfo
    );
    if (
      newPreorderResult == null ||
      (!!promoCode && newPreorderResult.promoInfo == null)
    ) {
      toast.error("Something went wrong, pleate try again");
      return;
    }

    if (!!promoCode && !!newPreorderResult.promoInfo.hasError) {
      toast.error(newPreorderResult.promoInfo.errorMessage);
      return;
    }

    setPreorderResult(newPreorderResult);
  };

  function formatTime(departDateTime) {
    if (!departDateTime) {
      return "";
    }

    return departDateTime.substring(11, 16);
  }

  function formatDuration(duration) {
    const total = Math.floor(Number(duration) || 0);
    const hh = Math.floor(total / 60);
    const mm = total % 60;
    return `${hh}h ${mm}m`;
  }

  function addPassenger() {
    let passengersCopy = [...passengers, null];
    setPassengers(passengersCopy);
  }

  function removePassenger(index) {
    let passengersCopy = [...passengers];
    passengersCopy.splice(index, 1);
    setPassengers(passengersCopy);
  }

  async function deletePassenger(passenger) {
    try {
      const result = await post(`/passengers/delete/${passenger.id}`);

      await fetchPassengers();
    } catch (err) {
      toast.error("Something Went Wrong. Please try again.");
    }
  }

  async function onSelectPassenger(e, index) {
    const isChecked = e.target.checked;
  
    if (isChecked) {
      const maxPassengersCount =
        flightSearchParams.passengers.adults +
        flightSearchParams.passengers.children +
        flightSearchParams.passengers.infants;
  
      const selectedPassengersCount = Object.keys(selectedPassengers).length;
  
      if (selectedPassengersCount >= maxPassengersCount) {
        toast.error(
          `You can only select ${maxPassengersCount} passenger${
            maxPassengersCount === 1 ? "" : "s"
          } based on your booking.`
        );
        return;
      }
  
      const validatedPassenger = await validatePassengers(passengers[index]);
      if (!validatedPassenger) {
        toast.error("Something went wrong, please try again.");
        return;
      }
  
      const passengersCopy = [...passengers];
      passengersCopy[index] = validatedPassenger;
      setPassengers(passengersCopy);
  
      setSelectedPassengers((prev) => ({ ...prev, [index]: validatedPassenger }));
    } else {
      setSelectedPassengers((prev) => {
        const copy = { ...prev };
        delete copy[index];
        return copy;
      });
    }
  }

  async function validatePassengers(selectedPassenger) {
    try {
      let validatedPassenger = await post("/passengers/validate", {
        id: selectedPassenger.id,
        preorderId: flightsPreorderResult.preorderInfo.preorderId,
      });
      return validatedPassenger;
    } catch (err) {
      if (err instanceof AppError && err.code === 30001) {
        showErrorInfo(err.message);
        return null;
      }

      return null;
    }
  }

  async function submitPassengersAndContactInfo() {
    let selectedPassengersCount = Object.keys(selectedPassengers).length;

    if (selectedPassengersCount === 0) {
      toast.error(
        "No Passengers Selected\nPlease select at least one passenger to continue."
      );
      return false;
    }

    const adultPassengers = Object.values(selectedPassengers).filter(
      (p) => p.type === "Adult"
    );
    const childPassengers = Object.values(selectedPassengers).filter(
      (p) => p.type === "Child"
    );
    const infantPassengers = Object.values(selectedPassengers).filter(
      (p) => p.type === "Infant"
    );

    let bookedAdults = flightSearchParams.passengers.adults || 0;
    let bookedChildren = flightSearchParams.passengers.children || 0;
    let bookedInfants = flightSearchParams.passengers.infants || 0;

    const plural = (n, singular, plural) => (n === 1 ? singular : plural);

    const joinWithAnd = (parts) => {
      if (parts.length === 0) return "";
      if (parts.length === 1) return parts[0];
      if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
      return `${parts.slice(0, parts.length - 1).join(", ")}, and ${
        parts[parts.length - 1]
      }`;
    };

    if (
      adultPassengers.length !== bookedAdults ||
      childPassengers.length !== bookedChildren ||
      infantPassengers.length !== bookedInfants
    ) {
      const parts = [];
      if (bookedAdults > 0)
        parts.push(
          `${bookedAdults} ${plural(bookedAdults, "adult", "adults")}`
        );
      if (bookedChildren > 0)
        parts.push(
          `${bookedChildren} ${plural(bookedChildren, "child", "children")}`
        );
      if (bookedInfants > 0)
        parts.push(
          `${bookedInfants} ${plural(bookedInfants, "infant", "infants")}`
        );

      toast.error(
        `Incorrect Passenger Count\nPlease select exactly ${joinWithAnd(
          parts
        )} to match your booking.`
      );
      return false;
    }

    if (!contactInfo) {
      toast.error(
        "Contact Info Missing\nPlease provide contact information to proceed."
      );
      return false;
    }

    return flightPreorder();
  }

  async function flightPreorder() {
    try {
      let preorderInfo = preorderResult.preorderInfo;
      preorderInfo.passengerInfos = Object.values(selectedPassengers);
      preorderInfo.contactInfo = contactInfo;

      let flightsPreorderResult = await post("/flights/preorder", preorderInfo);
      setPreorderResult(flightsPreorderResult);
      return flightsPreorderResult;
    } catch (err) {
      if (err instanceof AppError && err.code === 30001) {
        showErrorInfo(err.message);
        return null;
      }

      toast.error("Something went wrong, pleate try again.");
      return null;
    }
  }

  function showErrorInfo(errorMessage) {
    setErrorMessage(errorMessage);
    setShowError(true);
  }

  function onErrorConfirmed() {
    setShowError(false);
    navigate(buildFlightsUrl(flightSearchParams, 1));
  }

  async function onFinalStep() {
    if (!userData) {
      setOpenLoginModal(true);
      return;
    }

    const refreshedPreorder = await submitPassengersAndContactInfo();
    if (!refreshedPreorder) {
      return;
    }

    await handlePayment(refreshedPreorder);
  }

  const paymentNotify = async (paymentId) => {
    try {
      if (!paymentId) return;
      await post(`/payment/${paymentId}/notify`);
    } catch {
      // non-blocking
    }
  };

  const handlePayment = async (activePreorder = preorderResult) => {
    if (loading) return;

    // if (!selectedItinerary) {
    //   toast.error("No flights selected. Please choose a plan again.");
    //   navigate(buildFlightsUrl(flightSearchParams, 1), { replace: true });
    //   return;
    // }
    if (!activePreorder?.preorderInfo?.preorderId) {
      toast.error("Something Went Wrong. Please try again.");
      navigate(buildFlightsUrl(flightSearchParams, 1), { replace: true });
      return;
    }

    setLoading(true);
    try {
      const timezone = getTimezone();
      const submitOrderResponse = await post("/flights/submitOrder", {
        preorderId: activePreorder.preorderInfo.preorderId,
        timezone,
      });

      const revalidation = submitOrderResponse?.revalidateResult;
      const nextPriceInfo = submitOrderResponse?.priceInfo;
      const status = revalidation?.status;
      const revalidationAction = getFlightRevalidationAction(submitOrderResponse);

      if (nextPriceInfo && ["Price drop", "Price rise"].includes(status)) {
        setPreorderResult((previous) => ({ ...previous, priceInfo: nextPriceInfo }));
      }

      if (revalidationAction === FLIGHT_REVALIDATION_ACTION.RESEARCH) {
        setRevalidateDialog({
          revalidateResult: revalidation,
          priceInfo: nextPriceInfo,
          onResearched: () => navigate(buildFlightsUrl(flightSearchParams, 1), { replace: true }),
        });
        return;
      }

      if (revalidationAction === FLIGHT_REVALIDATION_ACTION.CONFIRM_PRICE_RISE) {
        const revisedPreorder = { ...activePreorder, priceInfo: nextPriceInfo };
        setRevalidateDialog({
          revalidateResult: revalidation,
          priceInfo: nextPriceInfo,
          onResearched: () => navigate(buildFlightsUrl(flightSearchParams, 1), { replace: true }),
          onContinued: () => handlePayment(revisedPreorder),
        });
        return;
      }

      const nextOrderId = submitOrderResponse?.orderId;
      if (!nextOrderId) {
        toast.error("We could not confirm this fare. Please search again or retry checkout.");
        return;
      }
      setOrderId(nextOrderId);

      const paymentResult = await post("/payment/pay", {
        orderId: nextOrderId,
        serviceType: "flights",
      });

      if (paymentResult?.isHit) {
        showErrorInfo(
          paymentResult?.riskResult?.message ||
          "This booking needs an additional account verification step."
        );
        return;
      }

      const payResult = paymentResult?.payResult;
      if (!payResult) {
        toast.error("Something went wrong, please try again.");
        return;
      }

      if (payResult.skipPayment === true) {
        navigate(`/my-bookings/flights/${nextOrderId}`, { replace: true });
        return;
      }

      const options = {
        key: payResult.appkey,
        order_id: payResult.providerOrderId,
        amount: payResult.amount,
        currency: payResult.currency,
        name: payResult.name,
        description: payResult.description,
        timeout: payResult.timeout,
        handler: async () => {
          await paymentNotify(payResult.paymentId);
          setLoading(false);
          navigate(`/my-bookings/flights/${nextOrderId}`, { replace: true });
        },
        prefill: {
          name: payResult.fullName,
          email: payResult.email,
          contact: payResult.contact,
        },
        theme: { color: "#3399cc" },
        // callback_url: `/my-bookings/flights/${orderId}`,
        // redirect: true
        modal: {
          ondismiss: function () {
            console.log("Checkout form closed");
            navigate(`/my-bookings/flights/${nextOrderId}`, { replace: true });
          },
        },
      };

      if (!window.Razorpay) {
        const loaded = await loadRazorpayScript();
        if (!loaded) {
          toast.error("Payment system failed to load. Please try again.");
          return;
        }
      }

      var rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function (response) {
        toast.error("Payment failed");
        navigate(`/my-bookings/flights/${nextOrderId}`, { replace: true });
      });

      rzp.open();
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        setOpenLoginModal(true);
        return;
      }
      if (err instanceof AppError && err.code === 30001) {
        showErrorInfo(err.message);
        return;
      }

      console.error("Payment Error:", err);
      toast.error("Something went wrong, please try again.");
    } finally {
      setLoading(false);
    }
  };

  async function passengerOnSubmit(values) {
    try {
      if (!values.id) {
        const result = await post("/passengers/create", values);
      } else {
        const result = await post(`/passengers/update/${values.id}`, values);
      }

      await fetchPassengers();
    } catch (err) {
      toast.error("Something Went Wrong. Please try again.");
    }
  }

  async function contactInfoOnSubmit(values) {
    try {
      if (!values.id) {
        const result = await post("/contactInfo/create", values);
      } else {
        const result = await post(`/contactInfo/update/${values.id}`, values);
      }

      await fetchContactInfo();
    } catch (err) {
      toast.error("Something Went Wrong. Please try again.");
    }
  }

  return (
    <div className="flights-page-content w-100">
      <div className="row px-3 flight-checkout-grid">
        <div className="col-12 p-2">
          <div className="steps-wrap">
            <div className="step">
              <FaCheck />
              <h5>Your Selection</h5>
            </div>
            <FaChevronRight color="#CBA135" />
            <div className="step">
              <i>2</i>
              <h5>Your Details</h5>
            </div>
            <FaChevronRight color="#051A37" />
            <div className="step active">
              <i>3</i>
              <h5>Final Step</h5>
            </div>
          </div>
        </div>
        <div className="col-lg-7 p-2 flight-checkout-main">
          <form className="guest-info">
            <div className="heading">
              <span className="checkout-section-kicker">Step 1</span>
              <h2>Traveller information</h2>
              <p>
                {userData
                  ? "Select every traveller included in this fare. Names must match the ID used at check-in."
                  : "Sign in to add passengers, confirm contact details, and complete your booking securely."}
              </p>
            </div>

            <div className="guest-inputs"></div>
          </form>

          {!userData && (
            <div className="checkout-login-prompt">
              <div>
                <span className="checkout-login-prompt__eyebrow">
                  Account required
                </span>
                <h3>Sign in to continue checkout</h3>
                <p>
                  Your passenger list and contact profile are saved to your
                  Teleport Now account, so checkout can stay fast and secure.
                </p>
              </div>
              <button
                type="button"
                className="checkout-login-prompt__button"
                onClick={() => setOpenLoginModal(true)}
              >
                Sign in
              </button>
            </div>
          )}

          {!!userData && !passengersLoading && (
            <div>
              <div>
                {passengers.map((passenger, idx) => {
                  return (
                    <PassengerCard
                      key={passenger?.id ?? idx}
                      passenger={passenger}
                      paxIndex={idx}
                      onSubmit={passengerOnSubmit}
                      editable={!passenger}
                      removePassenger={removePassenger}
                      deletePassenger={() => deletePassenger(passenger)}
                      onSelect={(e) => onSelectPassenger(e, idx)}
                      selectedPassengers={selectedPassengers}
                    />
                  );
                })}
              </div>

              <div className="add-passenger-card">
                <button
                  type="button"
                  className="btn add-btn"
                  onClick={addPassenger}
                >
                  <span className="add-icon">
                    <FaPlus />
                  </span>
                  <span>Add Passenger</span>
                </button>
              </div>
            </div>
          )}

          <div>
            {!!userData && !contactLoading && (
              <section className="checkout-contact-section" aria-labelledby="booking-contact-title">
                <div className="checkout-section-heading">
                  <span className="checkout-section-kicker">Step 2</span>
                  <div>
                    <h2 id="booking-contact-title">Booking contact</h2>
                    <p>We will send confirmations and important flight updates to this person.</p>
                  </div>
                </div>
                <ContactInfoCard
                  contactInfo={contactInfo}
                  onSubmit={contactInfoOnSubmit}
                  editable={!contactInfo}
                />
              </section>
            )}
          </div>

          <button
            onClick={() => {
              onFinalStep();
            }}
            className="final-step-btn"
          >
            {loading ? (
              <span className="loader"></span>
            ) : userData ? (
              "Review and pay"
            ) : (
              "Sign in to continue"
            )}
          </button>
        </div>
        <div className="col-lg-5 p-2 flight-checkout-summary">
          {(selectedItinerary?.journeys || []).map((journey) => {
            return journey.flights.map((flight, idx) => (
              <div className="flight-form-card" key={idx}>
                <div className="flight-item-box">
                  {/* Airline info */}
                  <div className="air-line">
                    <img
                      src={
                        flight.airline?.brandmarkUrl ||
                        flight.airline?.logo ||
                        "/images/icons/emirates.svg"
                      }
                      alt={flight.airline?.name}
                    />
                    <p>
                      {flight.airline?.name}, {flight.flightNo}
                    </p>
                  </div>

                  {/* Flight route */}
                  <div className="flight-row">
                    <div className="title">
                      <div className="time-wrapper">
                        <h5>{formatTime(journey?.departDateTime)}</h5>

                        {journey?.departDayCount > 0 && (
                          <span className="day-badge">
                            +{journey?.departDayCount}
                          </span>
                        )}
                      </div>
                      <p>{`${flight.departure.airport} ${flight.departure.terminal}`}</p>
                    </div>

                    <div className="time-duration">
                      <p>{formatDuration(flight.duration)}</p>
                      <img
                        src="/images/icons/flight-row.svg"
                        alt="flight-row"
                      />
                      {/* {flight.OTA_AirLowFareSearchRQ
                        ?.OriginDestinationInformation[0]
                        ?.StopLocations?.[0] && (
                        <p>
                          {
                            flight.OTA_AirLowFareSearchRQ
                              .OriginDestinationInformation[0].StopLocations[0]
                              .stayTime
                          }{" "}
                          in{" "}
                          {
                            flight.OTA_AirLowFareSearchRQ
                              .OriginDestinationInformation[0].StopLocations[0]
                              .LocationCode
                          }
                        </p>
                      )} */}
                    </div>

                    <div className="title to">
                      <div className="time-wrapper">
                        <h5>{formatTime(journey?.arriveDateTime)}</h5>

                        {journey?.arriveDayCount > 0 && (
                          <span className="day-badge">
                            +{journey?.arriveDayCount}
                          </span>
                        )}
                      </div>
                      <p>{`${flight.arrival.airport} ${flight.arrival.terminal}`}</p>
                    </div>
                  </div>

                  {/* Selects */}
                  {/* <div className="selects-wrapper">
                    <div className="baggage-icon">
                      <select name="baggage" id="baggage-select">
                        <option value="1">1 Baggage</option>
                        <option value="2">2 Baggage</option>
                      </select>
                    </div>
                    <div className="person-icon">
                      <select name="person" id="person-select">
                        <option value="1">1 Person</option>
                        <option value="2">2 Person</option>
                      </select>
                    </div>
                  </div> */}
                </div>
              </div>
            ));
          })}

          {/* Promo Code */}
          <ApplyPromoCode
            promoCode={preorderResult?.promoInfo?.promoUsage?.promoCode}
            onApply={onApply}
          />

          {/* Price Details */}
          <div className="total-price-box total-price-box--summary">
            <h6>Price Details</h6>
            {(preorderResult?.priceInfo?.priceDetails || []).map(
              (priceDetail) => {
                return (
                  <div>
                    <div className="price-row">
                      <p>{priceDetail.title}</p>
                      <p>{priceDetail.content}</p>
                    </div>
                    {!!priceDetail.children &&
                      priceDetail.children.map((child) => {
                        return (
                          <div className="sub-price-row">
                            <p>{child.title}</p>
                            <p>{child.content}</p>
                          </div>
                        );
                      })}
                  </div>
                );
              }
            )}
            {!!preorderResult?.priceInfo?.totalPrice && (
              <div className="total-price-row">
                <h2>{preorderResult.priceInfo.totalPrice.title}</h2>
                <h2>{preorderResult.priceInfo.totalPrice.content}</h2>
              </div>
            )}
          </div>

          {/* Cancellation Policy */}
          <div className="total-price-box">
            <h6>Cancellation Policy</h6>
            <h5>
              Cancellation fee: {preorderResult?.priceInfo?.totalPrice?.content}
            </h5>
            <p className="desc">
              This booking cannot be modified, and no refund will be given if
              you cancel it. If you fail to check in, a penalty equivalent to
              the cancellation fee will be charged. If you apply a discount to
              your booking, the cancellation fee will be based on the total you
              paid.
            </p>
          </div>
        </div>
        {/* <div className="col-lg-5 p-2">
          <div className="flight-form-card">
            <div className="flight-item-box">
              <div className="air-line">
                <img src="/images/icons/emirates.svg" alt="airline" />
                <p>Emirates, A380</p>
              </div>
              <div className="flight-row">
                <div className="title">
                  <h5>13:30</h5>
                  <p>LHR</p>
                </div>
                <div className="time-duration">
                  <p>8h 50m</p>
                  <img src="/images/icons/flight-row.svg" alt="flight-row" />
                  <p>5h in Karachi</p>
                </div>
                <div className="title to">
                  <h5>13:30</h5>
                  <p>LHR</p>
                </div>
              </div>
              <div className="selects-wrapper">
                <div className="baggage-icon">
                  <select name="baggage" id="baggage-select">
                    <option value="1">1 Baggage</option>
                    <option value="2">2 Baggage</option>
                  </select>
                </div>
                <div className="person-icon">
                  <select name="person" id="person-select">
                    <option value="1">1 Person</option>
                    <option value="2">2 Person</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="flight-form-card">
            <div className="flight-item-box">
              <div className="air-line">
                <img src="/images/icons/emirates.svg" alt="airline" />
                <p>Emirates, A380</p>
              </div>
              <div className="flight-row">
                <div className="title">
                  <h5>13:30</h5>
                  <p>LHR</p>
                </div>
                <div className="time-duration">
                  <p>8h 50m</p>
                  <img src="/images/icons/flight-row.svg" alt="flight-row" />
                  <p>5h in Karachi</p>
                </div>
                <div className="title to">
                  <h5>13:30</h5>
                  <p>LHR</p>
                </div>
              </div>
              <div className="selects-wrapper">
                <div className="baggage-icon">
                  <select name="baggage" id="baggage-select">
                    <option value="1">1 Baggage</option>
                    <option value="2">2 Baggage</option>
                  </select>
                </div>
                <div className="person-icon">
                  <select name="person" id="person-select">
                    <option value="1">1 Person</option>
                    <option value="2">2 Person</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="total-price-box">
            <h6>Price Details</h6>
            <div className="price-row">
              <p>1 room × 1 night</p>
              <p>Rs 45,994.46</p>
            </div>
            <div className="price-row">
              <p>VAT</p>
              <p>Rs 9,904.97</p>
            </div>
            <div className="total-price-row">
              <h2>Total</h2>
              <h2>Rs 55,899.43</h2>
            </div>
          </div>
          <div className="total-price-box">
            <h6>Cancellation Policy</h6>
            <h5>Cancellation fee: Rs 55,899.43</h5>
            <p className="desc">
              This booking cannot be modified, and no refund will be given if
              you cancel it. If you fail to check in, a penalty equivalent to
              the cancellation fee will be charged. If you apply a discount to
              your booking, the cancellation fee will be based on the total you
              paid.
            </p>
          </div>
        </div> */}
      </div>

      <ErrorModal
        open={showError}
        message={errorMessage}
        buttonText="OK"
        onConfirmed={onErrorConfirmed}
      />

      <LoginModal
        isOpen={openLoginModal}
        onClose={() => setOpenLoginModal(false)}
      />

      <RevalidateDialog
        visible={Boolean(revalidateDialog)}
        revalidateResult={revalidateDialog?.revalidateResult}
        priceInfo={revalidateDialog?.priceInfo}
        onResearched={revalidateDialog?.onResearched}
        onContinued={revalidateDialog?.onContinued}
        onClose={() => setRevalidateDialog(null)}
      />
    </div>
  );
};

export default CheckoutForm;
